import { getTypographyMetric, getTypographyMetricOverride } from '../../../../helpers';
import { IncentiveCampaignHeader } from 'containers/IncentiveCampaign/variants/IncentiveCampaignHeader';
import { useTheme } from 'styled-components';
import { Flex } from 'front-commons/ds/components/Flex';
import { Button } from 'front-commons/ds/components/Button';
import { Skeleton } from 'front-commons/ds/components/Skeleton';
import { Tag } from 'front-commons/ds/components/Tag';
import { Tooltip } from 'front-commons/ds/components/Tooltip';
import { Typography } from 'hype-ds';
import { useResponsive } from 'front-commons/hooks';
import { useNavigate } from 'react-router-dom';
import { ShowCategoriesMobile } from './components/Categories/components/ShowCategoriesMobile';
import { CategoriesDesktop } from './components/Categories/variants/Desktop';
import { useHeader } from '../../../../context';
import { shouldShowSuggestionCount } from './helpers';
import type { HeaderNavProps } from './interfaces';

export function HeaderNav({ catalogMenuItems, isSuggestionsProductNotifierExpired }: HeaderNavProps) {
	const {
		tracking: { selectContentGTM },
		pharmacy: { loadingPos },
	} = useHeader();

	const { fontSizes } = useTheme();
	const navigate = useNavigate();
	const { small } = useResponsive();

	return (
		<Flex
			padding={{ small: '1rem 0 1rem 1rem', medium: '1rem 0 0.875rem' }}
			gap="0.5rem"
			alignItems="center"
			raw={{
				overflowX: 'auto',
				scrollbarWidth: 'none',
				MsOverflowStyle: 'none',
				'&::-webkit-scrollbar': { display: 'none' },
				"&:has(div[data-testid='skeleton'])": { flexGrow: '1' },
			}}
			data-testid="header-nav-list"
			maxWidth="775px"
		>
			{!small && <CategoriesDesktop />}

			{small && <ShowCategoriesMobile />}

			{catalogMenuItems.map(({ href, label, status, counter, unavailableTooltip }) => {
				if (loadingPos) return <Skeleton key={label} height="2rem" />;

				const isAvailable = status === 'AVAILABLE';

				if (label === 'Bateu, Ganhou') {
					return <IncentiveCampaignHeader key={label} />;
				}

				if (isAvailable) {
					return (
						<Flex
							as={Button}
							display="flex"
							gap="8px"
							alignItems="center"
							justifyContent="center"
							position="relative"
							noShrink={true}
							cursor="pointer"
							borderRadius="8px"
							whiteSpace="nowrap"
							overflow="visible"
							raw={{
								'&&': {
									borderRadius: '8px',
									overflow: 'visible',
									backgroundColor: 'var(--surface-white)',
									"&[data-variant='none']": { padding: '0.25rem 0.5rem' },
									"&[data-status='unavailable']": { pointerEvents: 'none' },
									"&[data-status='available']:hover": { backgroundColor: 'var(--surface-accent-light)' },
									"&:has(div[data-testid='tag-container'])": {
										position: 'relative',
										div: { top: '-9px', right: '-10px', position: 'absolute' },
									},
									"[data-header-text='HeaderNav-text-1']": { display: 'inline' },
								},
								"[data-header-text='HeaderNav-text-1']": {
									fontSize: getTypographyMetricOverride(fontSizes['16']),
									lineHeight: getTypographyMetricOverride(fontSizes['24']),
								},
							}}
							key={label}
							data-status="available"
							variant="none"
							data-testid={`header-nav-item-${href.replace(/\//g, '')}`}
							onClick={() => {
								selectContentGTM('header_menu', label);
								navigate(href);
							}}
						>
							<Typography
								textAlign="inherit"
								fontSize={getTypographyMetric(fontSizes['16'])}
								fontWeight="600"
								lineHeight={getTypographyMetric(fontSizes['24'])}
								color="--hympar-blue-700"
								data-header-text="HeaderNav-text-1"
							>
								{label}
							</Typography>

							{shouldShowSuggestionCount(href, counter, isSuggestionsProductNotifierExpired) && (
								<Tag
									label={String(counter)}
									bgColor="--semantic-success-weak-light"
									labelOptions={{
										color: '--semantic-success-base',
										variant: 'Caption/Regular',
									}}
								/>
							)}
						</Flex>
					);
				}

				if (small) {
					return (
						<Flex
							as={Button}
							display="flex"
							gap="8px"
							alignItems="center"
							justifyContent="center"
							position="relative"
							noShrink={true}
							cursor="pointer"
							borderRadius="8px"
							whiteSpace="nowrap"
							overflow="visible"
							raw={{
								'&&': {
									borderRadius: '8px',
									overflow: 'visible',
									backgroundColor: 'var(--surface-white)',
									"&[data-variant='none']": { padding: '0.25rem 0.5rem' },
									"&[data-status='unavailable']": { pointerEvents: 'none' },
									"&[data-status='available']:hover": { backgroundColor: 'var(--surface-accent-light)' },
									"&:has(div[data-testid='tag-container'])": {
										position: 'relative',
										div: { top: '-9px', right: '-10px', position: 'absolute' },
									},
									"[data-header-text='HeaderNav-text-2']": { display: 'inline' },
								},
								"[data-header-text='HeaderNav-text-2']": {
									fontSize: getTypographyMetricOverride(fontSizes['16']),
									lineHeight: getTypographyMetricOverride(fontSizes['24']),
								},
							}}
							key={label}
							data-status="unavailable"
							variant="none"
							data-testid={`header-nav-item-${href.replace(/\//g, '')}`}
						>
							<Typography
								textAlign="inherit"
								fontSize={getTypographyMetric(fontSizes['16'])}
								fontWeight="600"
								lineHeight={getTypographyMetric(fontSizes['24'])}
								color="--greys-500"
								data-header-text="HeaderNav-text-2"
							>
								{label}
							</Typography>
						</Flex>
					);
				}

				return (
					<Tooltip key={label} content={unavailableTooltip} maxWidth="unset" showDialog={false} side="bottom">
						<Flex
							as={Button}
							display="flex"
							gap="8px"
							alignItems="center"
							justifyContent="center"
							position="relative"
							noShrink={true}
							cursor="pointer"
							borderRadius="8px"
							whiteSpace="nowrap"
							overflow="visible"
							raw={{
								'&&': {
									borderRadius: '8px',
									overflow: 'visible',
									backgroundColor: 'var(--surface-white)',
									"&[data-variant='none']": { padding: '0.25rem 0.5rem' },
									"&[data-status='unavailable']": { pointerEvents: 'none' },
									"&[data-status='available']:hover": { backgroundColor: 'var(--surface-accent-light)' },
									"&:has(div[data-testid='tag-container'])": {
										position: 'relative',
										div: { top: '-9px', right: '-10px', position: 'absolute' },
									},
									"[data-header-text='HeaderNav-text-3']": { display: 'inline' },
								},
								"[data-header-text='HeaderNav-text-3']": {
									fontSize: getTypographyMetricOverride(fontSizes['16']),
									lineHeight: getTypographyMetricOverride(fontSizes['24']),
								},
							}}
							key={label}
							data-status="unavailable"
							variant="none"
							data-testid={`header-nav-item-${href.replace(/\//g, '')}`}
						>
							<Typography
								textAlign="inherit"
								fontSize={getTypographyMetric(fontSizes['16'])}
								fontWeight="600"
								lineHeight={getTypographyMetric(fontSizes['24'])}
								color="--greys-500"
								data-header-text="HeaderNav-text-3"
							>
								{label}
							</Typography>
						</Flex>
					</Tooltip>
				);
			})}
		</Flex>
	);
}
